'use client';
import {useCallback, useRef} from 'react';

import {
  VodGroup,
  PostGroup,
  LiveGroup,
  CatchGroup,
  SearchDirectLinkGroup,
  RecommededStreamerGroup,
  SameNameGroup,
  RelatedSearchTermsGroup,
  CatchStoryGroup,
  SearchThemeGroup,
  LatestVideoGroup,
  TagSearchTagTrend,
  AdvertisementGroup,
  SuggestKeywordGroup,
  LiveNoResultSearchKeywordGroup,
} from '@/components/search/group';
import MyTabGroup from '@/components/my/common/group/TabGroup';
import {ErrorBoundary} from '@/components/common';
import {StreamerGroup} from '@/components/search/group/streamer-group';
import {NoSearchResults} from '@/components/search/common';
import {GameProfileGroup} from '@/components/search/group/game-profile-group';
import {useSearchClickLog} from '@/components/search/common/hooks/useSearchClickLog';
import {StreamerProfileGroup} from '@/components/search/group/streamer-profile-group';
import {useSearchGlobalStatus} from '@/components/search/SearchProvider';
import {RecommendContentsGroup} from '@/components/search/group/recommend-contents-group/index';

import {sendWebUvLog} from '@/utils/main';
import useGlobalUIStore from '@/stores';

const renderThemeSection = ({
  profileTheme,
  visibilityThemeGroup,
  visibilityLatestVideoGroup,
}) => {
  const canRenderTheme =
    visibilityThemeGroup && (profileTheme?.theme?.length ?? 0) > 0;

  const canRenderThemeVod =
    visibilityLatestVideoGroup && (profileTheme?.themeVod?.length ?? 0) > 0;

  if (!canRenderTheme && !canRenderThemeVod) {
    return null;
  }

  return (
    <>
      {canRenderTheme && (
        <ErrorBoundary>
          <SearchThemeGroup {...profileTheme} />
        </ErrorBoundary>
      )}
      {canRenderThemeVod && (
        <ErrorBoundary>
          <LatestVideoGroup
            latestVideoData={profileTheme.themeVod[0].contents}
            title={`${profileTheme.themeVod[0]?.searchNick || ''}의 최신 영상`}
            section='theme'
          />
        </ErrorBoundary>
      )}
    </>
  );
};

const SearchHome = ({
  className,
  currentTab,
  live,
  showSuggest,
  extraLive,
  streamerList,
  streamerRecommendList,
  vod,
  postList,
  catchList,
  catchStory,
  profileTheme,
  relatedWord,
  stopWord,
  keyword,
  searchType,
  hashType,
  tagname,
  fetchGameProfileThemeStreamerList,
  fetchGameScheduleList,
}) => {
  const prevTabRef = useRef('');
  const {sendTabClickLog} = useSearchClickLog();
  const {setRefreshTimestamp} = useGlobalUIStore();
  const {
    sessionKey,
    groupVisibility,
    stopWordLoading,
    gameProfileLoading,
    liveLoading,
    vodLoading,
    postLoading,
    streamerLoading,
    isTotalPage,
    isLiveTagSearchPage,
    isVodTagSearchPage,
  } = useSearchGlobalStatus();
  const {
    visibilitySuggestKeywordGroup,
    visibilityLiveNoResultSearchKeywordGroup,
    visibilityStreamerProfileGroup,
    visibilityDirectGroup,
    visibilityThemeGroup,
    visibilityAdvertisementGroup,
    visibilityGameProfileGroup,
    visibilityLatestVideoGroup,
    visibilitySameNameGroup,
    visibilityRecommendStreamerGroup,
    visibilitRecommendContentsGroup,
    visibilityLiveGroup,
    visibilityCatchStroyGroup,
    visibilityVodGroup,
    visibilityCatchGroup,
    visibilityPostGroup,
    visibilityStreamerGroup,
    visibilityTagSearchGroup,
    visibilityRelatedSearchKeywordGroup,
  } = groupVisibility(searchType, hashType);

  const isEmpty =
    (live?.length ?? 0) === 0 &&
    (extraLive?.length ?? 0) === 0 &&
    (streamerList?.length ?? 0) === 0 &&
    (vod?.length ?? 0) === 0 &&
    (catchList?.length ?? 0) === 0 &&
    (postList?.length ?? 0) === 0 &&
    (catchStory?.length ?? 0) === 0 &&
    (profileTheme?.recommendContents?.length ?? 0) === 0 &&
    (profileTheme?.homonym?.length ?? 0) === 0 &&
    (profileTheme?.gameProfile?.length ?? 0) === 0 &&
    Object.keys(profileTheme?.advertisement ?? {}).length === 0 &&
    (profileTheme?.esport?.length ?? 0) === 0 &&
    (profileTheme?.profile?.length ?? 0) === 0 &&
    (profileTheme?.recommendBj?.length ?? 0) === 0 &&
    (profileTheme?.relatedBj?.length ?? 0) === 0 &&
    (profileTheme?.theme?.length ?? 0) === 0 &&
    (profileTheme?.themeShortcuts?.length ?? 0) === 0 &&
    (profileTheme?.themeVod?.length ?? 0) === 0 &&
    stopWordLoading !== true &&
    gameProfileLoading !== true &&
    liveLoading !== true &&
    vodLoading !== true &&
    postLoading !== true &&
    streamerLoading !== true &&
    Object.keys(profileTheme?.latestVod ?? {}).length === 0;

  // SCKACT 로그
  const handleLogCallback = useCallback(
    (nextTab, queryString) => {
      let newTab = 'total';
      switch (nextTab) {
        case '통합검색':
          newTab = 'total';
          break;
        case 'LIVE':
          newTab = 'live';
          break;
        case 'VOD':
          newTab = 'vod';
          break;
        case '게시글':
          newTab = 'post';
          break;
        case '스트리머':
          newTab = 'bj';
          break;
      }
      let params = {
        tab: newTab,
        acttype: 'tab',
        location:
          isLiveTagSearchPage || isVodTagSearchPage ? 'hash' : 'total_search',
        actCode: 'tab',
        sessionKey: sessionKey,
      };

      sendTabClickLog(params);
      handleTabClicked(newTab, queryString);
    },
    [sessionKey, searchType, hashType],
  );

  /**
   * 검색 탭(=칩) 클릭 이벤트 핸들러
   * 1. 동일 탭 재클릭 시 데이터 갱신
   * 1-1. 검색페이지 첫 진입 후 바로 통합검색 탭 클릭 시 URL 변경으로 인한 자동 UV 로그 기록 (useCallAULog) > 데이터 갱신만 처리하도록 조건 분기
   * 2. 동일 탭 재클릭 시 UV 로그 기록
   *
   * @param {string} tabName - 클릭한 탭 이름
   * @param {string} queryString - 탭 관련 쿼리 파라미터
   */
  const handleTabClicked = (tabName, queryString) => {
    const currentSearch = window.location.search.substring(1);
    const isSameTab = currentSearch === queryString;
    // 검색페이지 첫 진입 후 바로 통합검색칩 클릭하는 경우
    const isInitialTotalTabClick = tabName === 'total' && !prevTabRef.current;

    // 데이터 재갱신
    if (isInitialTotalTabClick || isSameTab) {
      setRefreshTimestamp();
    }

    // webuv log 호출
    if (isSameTab) {
      sendWebUvLog(null);
    }

    prevTabRef.current = tabName;
  };

  const ThemeRender = renderThemeSection({
    profileTheme,
    visibilityThemeGroup,
    visibilityLatestVideoGroup,
  });

  const renderTotalSectionNoResult = () => {
    return (
      <NoSearchResults
        searchTab='total'
        isKeywordEntered={Boolean(keyword)}
        stopWord={stopWord.stopword}
        isIllegal={stopWord.isIllegal}
      />
    );
  };

  const shouldRenderNoResult =
    isTotalPage && (isEmpty || !keyword || stopWord.stopword);

  return (
    <>
      <div className='container_flex_wrap'>
        <MyTabGroup
          className='dept2_menu_wrap'
          type='search_route'
          tabs={currentTab}
          onHandle={(tabName, queryString) => {
            handleLogCallback(tabName, queryString);
          }}
        />
        <div className={'container_wrap'}>
          <div id='container' className={`_search ${className}`}>
            {/* 검색어 제안 */}
            {visibilitySuggestKeywordGroup && (
              <ErrorBoundary>
                <SuggestKeywordGroup stopWordInfo={stopWord} />
              </ErrorBoundary>
            )}
            {shouldRenderNoResult ? (
              renderTotalSectionNoResult()
            ) : (
              <>
                {/* Live 페이지에서 100% 매칭 검색결과가 없을 경우 검색어 노출 영역 ... 현타*/}
                {visibilityLiveNoResultSearchKeywordGroup && (
                  <ErrorBoundary>
                    <LiveNoResultSearchKeywordGroup
                      showSuggest={showSuggest}
                      live={live}
                      extraLive={extraLive}
                    />
                  </ErrorBoundary>
                )}

                {/* 바로가기 */}
                {visibilityDirectGroup && (
                  <ErrorBoundary>
                    <SearchDirectLinkGroup {...profileTheme} />
                  </ErrorBoundary>
                )}

                {/* 테마 상단고정인 경우 바로가기 섹션 아래에 테마 영역 위치 */}
                {profileTheme?.theme?.[0]?.isTop === 1 && ThemeRender}

                {/* 스트리머 프로필 */}
                {visibilityStreamerProfileGroup &&
                  profileTheme.profile &&
                  profileTheme.profile.length > 0 && (
                    <ErrorBoundary>
                      <StreamerProfileGroup {...profileTheme} />
                    </ErrorBoundary>
                  )}

                {/* 광고 영역 */}
                {visibilityAdvertisementGroup && (
                  <ErrorBoundary>
                    <AdvertisementGroup {...profileTheme} />
                  </ErrorBoundary>
                )}

                {/* 검색 키워드 기준 최신 영상 */}
                {visibilityLatestVideoGroup &&
                  profileTheme?.latestVod?.length > 0 && (
                    <ErrorBoundary>
                      <LatestVideoGroup
                        latestVideoData={profileTheme.latestVod}
                        title={`${profileTheme.profile[0]?.userNick || ''}의 최신 영상`}
                        section='streamer'
                      />
                    </ErrorBoundary>
                  )}

                {/* 테마 영역 디폴트 위치 */}
                {profileTheme?.theme?.[0]?.isTop !== 1 && ThemeRender}

                {/* 게임 프로필 */}
                {visibilityGameProfileGroup && (
                  <ErrorBoundary>
                    <GameProfileGroup
                      gameProfileList={profileTheme.gameProfile}
                      esportList={profileTheme.esport}
                      fetchGameProfileThemeStreamerList={
                        fetchGameProfileThemeStreamerList
                      }
                      fetchGameScheduleList={fetchGameScheduleList}
                    />
                  </ErrorBoundary>
                )}

                {/* 앗 혹시 찾으시는 분이...(= 동명이인 영역) */}
                {visibilitySameNameGroup && (
                  <ErrorBoundary>
                    <SameNameGroup {...profileTheme} />
                  </ErrorBoundary>
                )}

                {/* 관련 스트리머 */}
                {visibilityRecommendStreamerGroup && (
                  <ErrorBoundary>
                    <RecommededStreamerGroup
                      data={
                        searchType === 'streamer'
                          ? streamerRecommendList
                          : profileTheme.recommendBj
                      }
                      section='streamer'
                    />
                  </ErrorBoundary>
                )}

                {/* 테마 영역의 관련 스트리머*/}
                {visibilityLatestVideoGroup && (
                  <ErrorBoundary>
                    <RecommededStreamerGroup
                      data={profileTheme.themeBj}
                      section='theme'
                    />
                  </ErrorBoundary>
                )}

                {/* 검색 라이브 영역*/}
                {visibilityLiveGroup && (
                  <ErrorBoundary>
                    <LiveGroup
                      live={live}
                      extraLive={extraLive}
                      stopWord={stopWord.stopword}
                      isIllegal={stopWord.isIllegal}
                    />
                  </ErrorBoundary>
                )}

                {/* 관련 콘텐츠 */}
                {visibilitRecommendContentsGroup && (
                  <ErrorBoundary>
                    <RecommendContentsGroup {...profileTheme} />
                  </ErrorBoundary>
                )}

                {/* 캐치 스토리 */}
                {visibilityCatchStroyGroup && (
                  <ErrorBoundary>
                    <CatchStoryGroup catchStory={catchStory} />
                  </ErrorBoundary>
                )}

                {/* 캐치 - 캐치스토리가 없으면 해당 위치에 캐치 노출*/}
                {catchStory.length === 0 && visibilityCatchGroup && (
                  <ErrorBoundary>
                    <CatchGroup catchList={catchList} />
                  </ErrorBoundary>
                )}

                {/* VOD */}
                {visibilityVodGroup && (
                  <ErrorBoundary>
                    <VodGroup vod={vod} stopWord={stopWord.stopword} />
                  </ErrorBoundary>
                )}

                {/* 캐치 - 캐치스토리가 있으면 해당 위치에 캐치 노출*/}
                {catchStory.length > 0 && visibilityCatchGroup && (
                  <ErrorBoundary>
                    <CatchGroup catchList={catchList} />
                  </ErrorBoundary>
                )}

                {/* 게시글 */}
                {visibilityPostGroup && (
                  <ErrorBoundary>
                    <PostGroup
                      post={postList}
                      stopWord={stopWord.stopword}
                      isIllegal={stopWord.isIllegal}
                    />
                  </ErrorBoundary>
                )}

                {/* 스트리머 */}
                {visibilityStreamerGroup && (
                  <ErrorBoundary>
                    <StreamerGroup
                      streamer={streamerList}
                      stopWord={stopWord.stopword}
                      isIllegal={stopWord.isIllegal}
                    />
                  </ErrorBoundary>
                )}

                {/* 태그 검색 */}
                {visibilityTagSearchGroup &&
                  (isLiveTagSearchPage ? (
                    <ErrorBoundary>
                      <LiveGroup
                        live={live}
                        stopWord={stopWord.stopword}
                        isIllegal={stopWord.isIllegal}
                      />
                    </ErrorBoundary>
                  ) : (
                    isVodTagSearchPage && (
                      <ErrorBoundary>
                        <VodGroup
                          vod={vod}
                          stopWord={stopWord.stopword}
                          isIllegal={stopWord.isIllegal}
                        />
                      </ErrorBoundary>
                    )
                  ))}
              </>
            )}
          </div>

          {/* 태그 트랜드 */}
          {visibilityTagSearchGroup && (
            <ErrorBoundary>
              <TagSearchTagTrend />
            </ErrorBoundary>
          )}
          {/* 연관검색어 */}
          {visibilityRelatedSearchKeywordGroup && (
            <ErrorBoundary>
              <RelatedSearchTermsGroup relatedWord={relatedWord} />
            </ErrorBoundary>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchHome;
